import { type ReactNode } from 'react';

import { Textarea, type TextareaProps } from '@chakra-ui/react';
import { Controller, type FieldPath, type FieldValues } from 'react-hook-form';

import { FormFieldError } from '@/components/Form/FormFieldError';
import { FormFieldHelper } from '@/components/Form/FormFieldHelper';
import { FormFieldItem } from '@/components/Form/FormFieldItem';
import { FormFieldLabel } from '@/components/Form/FormFieldLabel';

import { type FieldCommonProps } from '../FormField.types';

export type FieldTextareaProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  type: 'textarea';
  label?: ReactNode;
  helper?: ReactNode;
} & Pick<
  TextareaProps,
  'placeholder' | 'size' | 'autoFocus' | 'rows' | 'sx' | 'backgroundColor'
> &
  FieldCommonProps<TFieldValues, TName>;

export const FieldTextarea = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: FieldTextareaProps<TFieldValues, TName>
) => {
  return (
    <Controller
      {...props}
      render={({ field }) => (
        <FormFieldItem>
          {!!props.label && <FormFieldLabel>{props.label}</FormFieldLabel>}
          <Textarea
            backgroundColor={props.backgroundColor}
            borderRadius="xl"
            sx={props.sx}
            size={props.size}
            placeholder={props.placeholder}
            autoFocus={props.autoFocus}
            rows={props.rows}
            {...field}
          />
          {!!props.helper && <FormFieldHelper>{props.helper}</FormFieldHelper>}
          <FormFieldError />
        </FormFieldItem>
      )}
    />
  );
};
